import React from 'react';
import TopBar from '../TopBar';
import Navbar from '../Navbar';
import Footer from '../Footer';
import SEO from '../SEO';

export default function PrivacyPolicy() {
  return (
    <div className="min-h-screen bg-gray-50">
      <SEO 
        title="Privacy Policy - Garud Stacks"
        description="Privacy policy for Garud Stacks. Learn how we protect your personal and business information."
        image="https://garudstacks.tech/img/logo.png"
        url="https://garudstacks.tech/privacy"
        keywords="privacy policy, data protection, garud stacks"
      />
      <TopBar />
      <Navbar />

      {/* Hero Header */}
      <div className="relative bg-white border-b border-gray-200 py-16 md:py-20 overflow-hidden">
        <div className="absolute top-0 right-0 w-[500px] h-[500px] bg-amber-500/5 rounded-full blur-[150px] pointer-events-none z-0"></div>
        <div className="absolute bottom-0 left-0 w-[400px] h-[400px] bg-blue-600/5 rounded-full blur-[150px] pointer-events-none z-0"></div>

        <div className="max-w-4xl mx-auto px-6 md:px-8 relative z-10">
          <div className="inline-flex items-center border border-gray-300 px-4 py-2 bg-gray-50 text-xs font-bold uppercase tracking-widest text-amber-700 mb-6 rounded-md shadow-sm hover:border-amber-500 transition-all duration-300">
            <span className="w-2 h-2 bg-amber-500 rounded-full mr-3 animate-pulse"></span>
            Legal Documentation
          </div>
          <h1 className="text-4xl sm:text-5xl lg:text-6xl font-extrabold leading-tight uppercase mb-4 text-gray-900">
            <span className="text-transparent bg-clip-text bg-gradient-to-r from-gray-900 to-amber-700">Privacy Policy</span>
          </h1>
          <p className="text-gray-600 text-base md:text-lg">Last updated: April 2026 • Your data privacy matters to us</p>
        </div>
      </div>

      {/* Content */}
      <div className="max-w-4xl mx-auto px-6 md:px-8 py-16 md:py-24">
        <div className="space-y-8">
          
          <section className="bg-white rounded-2xl p-8 md:p-10 shadow-lg border border-gray-100 hover:shadow-xl transition-all duration-300">
            <h2 className="text-2xl md:text-3xl font-extrabold text-gray-900 mb-6 flex items-center gap-3">
              <span className="w-10 h-10 bg-gradient-to-br from-amber-500 to-amber-600 text-white rounded-lg flex items-center justify-center font-bold text-sm">01</span>
              Introduction
            </h2>
            <p className="text-gray-700 leading-relaxed text-lg">
              Garud Stacks Pvt. Ltd. Company is committed to protecting your privacy. This Privacy Policy explains how we collect, use, disclose, and safeguard your information when you visit our website, use our services, or interact with us in other ways. Please read this policy carefully to understand our views and practices regarding your personal data and how we will treat it.
            </p>
          </section>

          <section className="bg-white rounded-2xl p-8 md:p-10 shadow-lg border border-gray-100 hover:shadow-xl transition-all duration-300">
            <h2 className="text-2xl md:text-3xl font-extrabold text-gray-900 mb-6 flex items-center gap-3">
              <span className="w-10 h-10 bg-gradient-to-br from-amber-500 to-amber-600 text-white rounded-lg flex items-center justify-center font-bold text-sm">02</span>
              Information We Collect
            </h2>
            <p className="text-gray-700 leading-relaxed text-lg mb-6">
              We may collect and process the following data about you:
            </p>
            <ul className="space-y-4 text-gray-700">
              <li className="flex gap-4 items-start">
                <span className="text-amber-600 font-bold mt-0.5 text-lg leading-none">▪</span>
                <div>
                  <p className="font-semibold text-gray-900">Personal Identification Information:</p>
                  <p>Name, email address, phone number, company name, job title, and other identifying information</p>
                </div>
              </li>
              <li className="flex gap-4 items-start">
                <span className="text-amber-600 font-bold mt-0.5 text-lg leading-none">▪</span>
                <div>
                  <p className="font-semibold text-gray-900">Technical Data:</p>
                  <p>IP address, browser type and version, operating system, device type, and other technology on devices used to access our website</p>
                </div>
              </li>
              <li className="flex gap-4 items-start">
                <span className="text-amber-600 font-bold mt-0.5 text-lg leading-none">▪</span>
                <div>
                  <p className="font-semibold text-gray-900">Usage Data:</p>
                  <p>Information about how you use our website, products, and services</p>
                </div>
              </li>
              <li className="flex gap-4 items-start">
                <span className="text-amber-600 font-bold mt-0.5 text-lg leading-none">▪</span>
                <div>
                  <p className="font-semibold text-gray-900">Marketing and Communications Data:</p>
                  <p>Your preferences in receiving marketing from us and your communication preferences</p>
                </div>
              </li>
            </ul>
          </section>

          <section className="bg-white rounded-2xl p-8 md:p-10 shadow-lg border border-gray-100 hover:shadow-xl transition-all duration-300">
            <h2 className="text-2xl md:text-3xl font-extrabold text-gray-900 mb-6 flex items-center gap-3">
              <span className="w-10 h-10 bg-gradient-to-br from-amber-500 to-amber-600 text-white rounded-lg flex items-center justify-center font-bold text-sm">03</span>
              Consequences of Not Providing Information
            </h2>
            <p className="text-gray-700 leading-relaxed text-lg mb-6">
              If you choose not to provide certain personal information that is mandatory to fulfill your request, we may not be able to complete the following:
            </p>
            <ul className="space-y-3 text-gray-700">
              <li className="flex gap-3 items-start">
                <span className="text-amber-600 font-bold mt-0.5 text-lg leading-none">•</span>
                <span>Provide you with access to our services and features</span>
              </li>
              <li className="flex gap-3 items-start">
                <span className="text-amber-600 font-bold mt-0.5 text-lg leading-none">•</span>
                <span>Process your service requests or inquiries</span>
              </li>
              <li className="flex gap-3 items-start">
                <span className="text-amber-600 font-bold mt-0.5 text-lg leading-none">•</span>
                <span>Send you important service updates or security notices</span>
              </li>
              <li className="flex gap-3 items-start">
                <span className="text-amber-600 font-bold mt-0.5 text-lg leading-none">•</span>
                <span>Provide customer support and technical assistance</span>
              </li>
            </ul>
          </section>

          <section className="bg-white rounded-2xl p-8 md:p-10 shadow-lg border border-gray-100 hover:shadow-xl transition-all duration-300">
            <h2 className="text-2xl md:text-3xl font-extrabold text-gray-900 mb-6 flex items-center gap-3">
              <span className="w-10 h-10 bg-gradient-to-br from-amber-500 to-amber-600 text-white rounded-lg flex items-center justify-center font-bold text-sm">04</span>
              How We Use Your Information
            </h2>
            <p className="text-gray-700 leading-relaxed text-lg mb-6">
              We use the information we collect for various purposes to provide and improve our services:
            </p>
            <ul className="space-y-3 text-gray-700">
              <li className="flex gap-3 items-start">
                <span className="text-amber-600 font-bold mt-0.5 text-lg leading-none">•</span>
                <span>To provide, operate, and maintain our website and services</span>
              </li>
              <li className="flex gap-3 items-start">
                <span className="text-amber-600 font-bold mt-0.5 text-lg leading-none">•</span>
                <span>To improve, personalize, and expand our website and services</span>
              </li>
              <li className="flex gap-3 items-start">
                <span className="text-amber-600 font-bold mt-0.5 text-lg leading-none">•</span>
                <span>To understand and analyze how you use our website and services</span>
              </li>
              <li className="flex gap-3 items-start">
                <span className="text-amber-600 font-bold mt-0.5 text-lg leading-none">•</span>
                <span>To develop new products, services, features, and functionality</span>
              </li>
              <li className="flex gap-3 items-start">
                <span className="text-amber-600 font-bold mt-0.5 text-lg leading-none">•</span>
                <span>To communicate with you regarding updates and service improvements</span>
              </li>
              <li className="flex gap-3 items-start">
                <span className="text-amber-600 font-bold mt-0.5 text-lg leading-none">•</span>
                <span>To send marketing and promotional information (with your consent)</span>
              </li>
              <li className="flex gap-3 items-start">
                <span className="text-amber-600 font-bold mt-0.5 text-lg leading-none">•</span>
                <span>To process transactions and manage your orders</span>
              </li>
            </ul>
          </section>

          <section className="bg-white rounded-2xl p-8 md:p-10 shadow-lg border border-gray-100 hover:shadow-xl transition-all duration-300">
            <h2 className="text-2xl md:text-3xl font-extrabold text-gray-900 mb-6 flex items-center gap-3">
              <span className="w-10 h-10 bg-gradient-to-br from-amber-500 to-amber-600 text-white rounded-lg flex items-center justify-center font-bold text-sm">05</span>
              Sharing Your Information
            </h2>
            <p className="text-gray-700 leading-relaxed text-lg mb-6">
              We may share your information with:
            </p>
            <ul className="space-y-4 text-gray-700">
              <li className="flex gap-4 items-start">
                <span className="text-amber-600 font-bold mt-0.5">▪</span>
                <div>
                  <p className="font-semibold text-gray-900">Service Providers:</p>
                  <p>We may share your information with third-party service providers to perform tasks on our behalf and assist us in providing our services</p>
                </div>
              </li>
              <li className="flex gap-4 items-start">
                <span className="text-amber-600 font-bold mt-0.5">▪</span>
                <div>
                  <p className="font-semibold text-gray-900">Business Transfers:</p>
                  <p>We may share or transfer your information in connection with, or during negotiations of, any merger, sale of company assets, financing, or acquisition of all or a portion of our business</p>
                </div>
              </li>
              <li className="flex gap-4 items-start">
                <span className="text-amber-600 font-bold mt-0.5">▪</span>
                <div>
                  <p className="font-semibold text-gray-900">Legal Requirements:</p>
                  <p>We may disclose your information where we are legally required to do so in order to comply with applicable law, governmental requests, judicial proceedings, or court orders</p>
                </div>
              </li>
            </ul>
          </section>

          <section className="bg-white rounded-2xl p-8 md:p-10 shadow-lg border border-gray-100 hover:shadow-xl transition-all duration-300">
            <h2 className="text-2xl md:text-3xl font-extrabold text-gray-900 mb-6 flex items-center gap-3">
              <span className="w-10 h-10 bg-gradient-to-br from-amber-500 to-amber-600 text-white rounded-lg flex items-center justify-center font-bold text-sm">06</span>
              Third-Party Service Providers
            </h2>
            <p className="text-gray-700 leading-relaxed text-lg mb-6">
              We work with trusted third-party service providers to help us deliver and improve our services. These providers assist us with:
            </p>
            <ul className="space-y-4 text-gray-700">
              <li className="flex gap-4 items-start">
                <span className="text-amber-600 font-bold mt-0.5">▪</span>
                <div>
                  <p className="font-semibold text-gray-900">Analytics Services:</p>
                  <p>Google Analytics helps us understand how visitors use our website and improve performance</p>
                </div>
              </li>
              <li className="flex gap-4 items-start">
                <span className="text-amber-600 font-bold mt-0.5">▪</span>
                <div>
                  <p className="font-semibold text-gray-900">Cloud Services:</p>
                  <p>We use secure cloud hosting providers to maintain and back up our systems and data</p>
                </div>
              </li>
              <li className="flex gap-4 items-start">
                <span className="text-amber-600 font-bold mt-0.5">▪</span>
                <div>
                  <p className="font-semibold text-gray-900">Email and Marketing Services:</p>
                  <p>Third-party providers help us send communications and manage our marketing campaigns</p>
                </div>
              </li>
              <li className="flex gap-4 items-start">
                <span className="text-amber-600 font-bold mt-0.5">▪</span>
                <div>
                  <p className="font-semibold text-gray-900">Payment Processing:</p>
                  <p>Secure payment processors handle financial transactions on our behalf</p>
                </div>
              </li>
            </ul>
            <p className="text-gray-700 leading-relaxed text-lg mt-6">
              All third-party service providers are contractually required to maintain the confidentiality and security of your personal data and are not permitted to use it for purposes other than those specified in our instructions.
            </p>
          </section>

          <section className="bg-white rounded-2xl p-8 md:p-10 shadow-lg border border-gray-100 hover:shadow-xl transition-all duration-300">
            <h2 className="text-2xl md:text-3xl font-extrabold text-gray-900 mb-6 flex items-center gap-3">
              <span className="w-10 h-10 bg-gradient-to-br from-amber-500 to-amber-600 text-white rounded-lg flex items-center justify-center font-bold text-sm">07</span>
              Security of Your Information
            </h2>
            <p className="text-gray-700 leading-relaxed text-lg">
              We use administrative, technical, and physical security measures to help protect your personal information. While we have taken reasonable steps to secure the personal information you provide to us, please be aware that despite our efforts, no security measures are perfect or impenetrable, and no method of data transmission can be guaranteed against any interception or other type of misuse.
            </p>
          </section>

          <section className="bg-white rounded-2xl p-8 md:p-10 shadow-lg border border-gray-100 hover:shadow-xl transition-all duration-300">
            <h2 className="text-2xl md:text-3xl font-extrabold text-gray-900 mb-6 flex items-center gap-3">
              <span className="w-10 h-10 bg-gradient-to-br from-amber-500 to-amber-600 text-white rounded-lg flex items-center justify-center font-bold text-sm">08</span>
              Your Data Protection Rights
            </h2>
            <p className="text-gray-700 leading-relaxed text-lg mb-6">
              Depending on your location, you may have the following rights regarding your personal data:
            </p>
            <ul className="space-y-3 text-gray-700">
              <li className="flex gap-3 items-start">
                <span className="text-amber-600 font-bold mt-0.5 text-lg leading-none">•</span>
                <span><span className="font-semibold text-gray-900">Right to access:</span> You have the right to request copies of your personal data</span>
              </li>
              <li className="flex gap-3 items-start">
                <span className="text-amber-600 font-bold mt-0.5 text-lg leading-none">•</span>
                <span><span className="font-semibold text-gray-900">Right to rectification:</span> You have the right to request that we correct any inaccurate information</span>
              </li>
              <li className="flex gap-3 items-start">
                <span className="text-amber-600 font-bold mt-0.5 text-lg leading-none">•</span>
                <span><span className="font-semibold text-gray-900">Right to erasure:</span> You have the right to request that we erase your personal data, under certain conditions</span>
              </li>
              <li className="flex gap-3 items-start">
                <span className="text-amber-600 font-bold mt-0.5 text-lg leading-none">•</span>
                <span><span className="font-semibold text-gray-900">Right to restrict processing:</span> You have the right to request that we restrict processing of your personal data</span>
              </li>
              <li className="flex gap-3 items-start">
                <span className="text-amber-600 font-bold mt-0.5 text-lg leading-none">•</span>
                <span><span className="font-semibold text-gray-900">Right to data portability:</span> You have the right to request that we transfer the data we have collected to another organization</span>
              </li>
            </ul>
          </section>

          <section className="bg-white rounded-2xl p-8 md:p-10 shadow-lg border border-gray-100 hover:shadow-xl transition-all duration-300">
            <h2 className="text-2xl md:text-3xl font-extrabold text-gray-900 mb-6 flex items-center gap-3">
              <span className="w-10 h-10 bg-gradient-to-br from-amber-500 to-amber-600 text-white rounded-lg flex items-center justify-center font-bold text-sm">09</span>
              Cookies and Tracking Technologies
            </h2>
            <p className="text-gray-700 leading-relaxed text-lg">
              We use cookies and similar tracking technologies to track the activity on our website and hold certain information. Cookies are files with a small amount of data which may include an anonymous unique identifier. You can instruct your browser to refuse all cookies or to indicate when a cookie is being sent. However, if you do not accept cookies, you may not be able to use some portions of our Service.
            </p>
          </section>

          <section className="bg-white rounded-2xl p-8 md:p-10 shadow-lg border border-gray-100 hover:shadow-xl transition-all duration-300">
            <h2 className="text-2xl md:text-3xl font-extrabold text-gray-900 mb-6 flex items-center gap-3">
              <span className="w-10 h-10 bg-gradient-to-br from-amber-500 to-amber-600 text-white rounded-lg flex items-center justify-center font-bold text-sm">10</span>
              Data Retention
            </h2>
            <p className="text-gray-700 leading-relaxed text-lg mb-6">
              We retain your personal data only for as long as necessary to fulfill the purposes outlined in this Privacy Policy, including to satisfy legal, accounting, and reporting requirements. The retention period may vary depending on the context:
            </p>
            <ul className="space-y-3 text-gray-700">
              <li className="flex gap-3 items-start">
                <span className="text-amber-600 font-bold mt-0.5 text-lg leading-none">•</span>
                <span><span className="font-semibold text-gray-900">Contact Information:</span> Retained for the duration of our relationship plus 3 years for business records</span>
              </li>
              <li className="flex gap-3 items-start">
                <span className="text-amber-600 font-bold mt-0.5 text-lg leading-none">•</span>
                <span><span className="font-semibold text-gray-900">Transaction Data:</span> Retained for 7 years as per regulatory compliance requirements</span>
              </li>
              <li className="flex gap-3 items-start">
                <span className="text-amber-600 font-bold mt-0.5 text-lg leading-none">•</span>
                <span><span className="font-semibold text-gray-900">Usage and Analytics Data:</span> Retained for 12 months to analyze trends and improve services</span>
              </li>
              <li className="flex gap-3 items-start">
                <span className="text-amber-600 font-bold mt-0.5 text-lg leading-none">•</span>
                <span><span className="font-semibold text-gray-900">Marketing Preferences:</span> Retained until you unsubscribe or request removal</span>
              </li>
            </ul>
            <p className="text-gray-700 leading-relaxed text-lg mt-6">
              Once the retention period expires, we will securely delete or anonymize your personal data unless we are required to retain it by law.
            </p>
          </section>

          <section className="bg-white rounded-2xl p-8 md:p-10 shadow-lg border border-gray-100 hover:shadow-xl transition-all duration-300">
            <h2 className="text-2xl md:text-3xl font-extrabold text-gray-900 mb-6 flex items-center gap-3">
              <span className="w-10 h-10 bg-gradient-to-br from-amber-500 to-amber-600 text-white rounded-lg flex items-center justify-center font-bold text-sm">11</span>
              Changes to This Privacy Policy
            </h2>
            <p className="text-gray-700 leading-relaxed text-lg">
              We may update our Privacy Policy from time to time. We will notify you of any changes by posting the new Privacy Policy on this page. You are advised to review this Privacy Policy periodically for any changes. Changes to this Privacy Policy are effective when they are posted on this page.
            </p>
          </section>

          <section className="bg-white rounded-2xl p-8 md:p-10 shadow-lg border border-gray-100 hover:shadow-xl transition-all duration-300">
            <h2 className="text-2xl md:text-3xl font-extrabold text-gray-900 mb-6 flex items-center gap-3">
              <span className="w-10 h-10 bg-gradient-to-br from-amber-500 to-amber-600 text-white rounded-lg flex items-center justify-center font-bold text-sm">12</span>
              Right to Withdraw Consent
            </h2>
            <p className="text-gray-700 leading-relaxed text-lg mb-6">
              Where we process your personal data based on your consent (such as for marketing communications), you have the right to withdraw that consent at any time without penalty. Withdrawing your consent will not affect the lawfulness of the processing we conducted before your withdrawal.
            </p>
            <p className="text-gray-700 leading-relaxed text-lg mb-6">
              To withdraw your consent or update your preferences, you can:
            </p>
            <ul className="space-y-3 text-gray-700">
              <li className="flex gap-3 items-start">
                <span className="text-amber-600 font-bold mt-0.5 text-lg leading-none">•</span>
                <span>Click the "unsubscribe" link in any marketing email we send you</span>
              </li>
              <li className="flex gap-3 items-start">
                <span className="text-amber-600 font-bold mt-0.5 text-lg leading-none">•</span>
                <span>Contact us directly at garudstacks@gmail.com with your request</span>
              </li>
              <li className="flex gap-3 items-start">
                <span className="text-amber-600 font-bold mt-0.5 text-lg leading-none">•</span>
                <span>Use our preference center (if available) to manage your communication preferences</span>
              </li>
            </ul>
          </section>

          <section className="bg-gradient-to-br from-amber-50 to-white rounded-2xl p-8 md:p-10 shadow-lg border border-amber-200 hover:shadow-xl transition-all duration-300">
            <h2 className="text-2xl md:text-3xl font-extrabold text-gray-900 mb-6 flex items-center gap-3">
              <span className="w-10 h-10 bg-gradient-to-br from-amber-500 to-amber-600 text-white rounded-lg flex items-center justify-center font-bold text-sm">13</span>
              Contact Us
            </h2>
            <p className="text-gray-700 leading-relaxed text-lg mb-8">
              If you have any questions about this Privacy Policy, please contact us at:
            </p>
            <div className="bg-white border border-amber-300 p-6 rounded-lg space-y-4">
              <p className="text-gray-800 flex items-center gap-3"><span className="text-amber-600 font-bold text-lg">✉</span> <span><span className="font-semibold text-gray-900">Email:</span> garudstacks@gmail.com</span></p>
              <p className="text-gray-800 flex items-center gap-3"><span className="text-amber-600 font-bold text-lg">☎</span> <span><span className="font-semibold text-gray-900">Phone:</span> +91 95229 50225</span></p>
              <p className="text-gray-800 flex items-center gap-3"><span className="text-amber-600 font-bold text-lg">☎</span> <span><span className="font-semibold text-gray-900">Phone:</span> +91 98065 20216</span></p>
              <p className="text-gray-800 flex items-center gap-3"><span className="text-amber-600 font-bold text-lg">📍</span> <span><span className="font-semibold text-gray-900">Address:</span> Panchwati Colony Phase 3, Karond, Bhopal, MP 462038</span></p>
            </div>
          </section>

        </div>
      </div>

      <Footer />
    </div>
  );
}
